import { Fragment, type Ref } from 'react'
import { indirizzoLogo } from '../servizi/stampa.ts'
import type { FoglioModulo as Foglio, LogoDelProgetto, RigaFoglioModulo } from '../servizi/modulo-progetto.ts'

/**
 * Foglio del modulo del destinatario di un progetto (492), come lo compone l'API: lo usano l'anteprima
 * vuota nella pagina del progetto e la pagina di stampa dalla scheda. L'impaginazione segue i due
 * moduli FAMI 2021-2027 e il PDF/A del Job dei lavori (apps/lavori/src/stampa/modulo-progetto.ts):
 * loghi, fondo, barra del titolo, tabella dell'intestazione, tabella dei dati con i numeri e gli
 * asterischi, data e firma; l'informativa su un foglio suo, di nuovo data e firma, gli allegati.
 *
 * Solo classi `modulo-` di stile-stampa.css. Il foglio usa i neutri fissi del tema, come quello della
 * scheda: in tema scuro resta bianco con il testo nero.
 */

function Cella({ valore, segnaposto }: { valore: string | null; segnaposto: string | null }) {
  if (valore) return <td className="modulo-valore">{valore}</td>
  return <td className="modulo-valore">{segnaposto && <span className="modulo-segnaposto">{segnaposto}</span>}</td>
}

function Riga({ riga: r }: { riga: RigaFoglioModulo }) {
  const numero = (n: number | null) => <td className="modulo-numero">{n ?? ''}</td>
  const etichetta = (testo: string, facoltativo: boolean) => <th scope="row">{testo}{facoltativo ? '*' : ''}</th>
  switch (r.tipo) {
    case 'testo':
      return <tr><td colSpan={3} className="modulo-riga-testo">{r.testo}</td></tr>
    case 'vulnerabilita':
      return (
        <>
          <tr><td colSpan={3} className="modulo-riga-nota">{r.testo}</td></tr>
          {r.voci.map((v, i) => (
            <tr key={i}>{numero(null)}<td colSpan={2} className="modulo-valore">{v ?? ''}</td></tr>
          ))}
        </>
      )
    case 'note':
      // Sempre vuota: si scrive a mano.
      return <tr className="modulo-riga-alta">{numero(r.numero)}{etichetta(r.etichetta, r.facoltativo)}<td className="modulo-valore" /></tr>
    case 'campo':
      if (r.caselle) {
        return (
          <tr>
            {numero(r.numero)}{etichetta(r.etichetta, r.facoltativo)}
            <td className="modulo-valore">
              <span className="modulo-caselle">
                {r.caselle.map((c) => (
                  <span key={c.voce}>
                    {c.voce}{' '}
                    <span className={`modulo-casella${c.segnata ? ' modulo-casella-segnata' : ''}`}
                          role="img" aria-label={c.segnata ? 'segnata' : 'vuota'}>{c.segnata ? '✕' : ''}</span>
                  </span>
                ))}
              </span>
            </td>
          </tr>
        )
      }
      return (
        <>
          {r.celle.map((c, i) => (
            <tr key={i}>
              {numero(i === 0 ? r.numero : null)}
              {etichetta(c.etichetta ?? r.etichetta, r.facoltativo)}
              <Cella valore={c.valore} segnaposto={r.segnaposto} />
            </tr>
          ))}
        </>
      )
  }
}

function DataFirma() {
  return (
    <div className="modulo-firma">
      <p>Data <span className="modulo-riga-scrittura" /></p>
      <p>Firma <span className="modulo-riga-scrittura modulo-riga-lunga" /></p>
    </div>
  )
}

export function FoglioModulo({ foglio: f, loghi, fascia, etichetta }: {
  foglio: Foglio
  loghi: LogoDelProgetto[]
  /** La pagina di stampa aspetta i loghi prima di stampare. */
  fascia?: Ref<HTMLDivElement>
  etichetta: string
}) {
  return (
    <article className="modulo-foglio" aria-label={etichetta}>
      <div className="modulo-pagina">
        {f.sopratitolo && <p className="modulo-sopratitolo">{f.sopratitolo}</p>}
        {loghi.length > 0 && (
          <div className="modulo-loghi" ref={fascia}>
            {loghi.map((l) => <img key={l.id} src={indirizzoLogo(l)} alt={l.nome} />)}
          </div>
        )}
        {f.fondo && <p className="modulo-fondo">{f.fondo}</p>}
        <p className="modulo-barra">{f.titolo_scheda}</p>
        <table className="modulo-intestazione">
          <tbody>
            <tr><th scope="row">Codice identificativo di progetto</th><td>{f.intestazione.codice}</td></tr>
            <tr><th scope="row">Beneficiario Finale</th><td>{f.intestazione.beneficiario}</td></tr>
            <tr><th scope="row">Titolo del progetto</th><td>{f.intestazione.titolo}</td></tr>
          </tbody>
        </table>
        <table className="modulo-dati">
          <tbody>
            {f.righe.map((r, i) => <Fragment key={i}><Riga riga={r} /></Fragment>)}
          </tbody>
        </table>
        {f.facoltativi && <p className="modulo-facoltativi">*dati facoltativi</p>}
        <DataFirma />
      </div>
      <div className="modulo-pagina modulo-informativa">
        {f.informativa.split('\n').map((p, i) => <p key={i}>{p}</p>)}
        <DataFirma />
        {f.allegati.length > 0 && <p className="modulo-allegati">ALLEGARE: {f.allegati.join(', ')}</p>}
      </div>
      <footer className="modulo-piede">{f.progetto}</footer>
    </article>
  )
}
